import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cartAPI } from '../../services/api';

// Async Thunks
export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cartAPI.getCart();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch cart');
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async ({ productId, quantity = 1, options = {} }, { rejectWithValue }) => {
    try {
      const response = await cartAPI.addItem({ productId, quantity, options });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add item to cart');
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateCartItem',
  async ({ productId, quantity, options }, { rejectWithValue }) => {
    try {
      const response = await cartAPI.updateItem({ productId, quantity, options });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update cart item');
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await cartAPI.removeItem(productId);
      return { productId, cart: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove item from cart');
    }
  }
);

export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cartAPI.clearCart();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to clear cart');
    }
  }
);

export const applyCoupon = createAsyncThunk(
  'cart/applyCoupon',
  async (couponCode, { rejectWithValue }) => {
    try {
      const response = await cartAPI.applyCoupon(couponCode);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Invalid coupon code');
    }
  }
);

export const removeCoupon = createAsyncThunk(
  'cart/removeCoupon',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cartAPI.removeCoupon();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove coupon');
    }
  }
);

// Initial State
const initialState = {
  items: [],
  totalItems: 0,
  subtotal: 0,
  tax: 0,
  shipping: 0,
  discount: 0,
  total: 0,
  appliedCoupon: null,
  deliveryOption: 'standard', // 'standard', 'express', 'pickup'
  installationOptions: {},
  loading: false,
  error: null,
  isOpen: false, // For cart sidebar
};

// Helper function to calculate totals
const calculateTotals = (items, appliedCoupon = null, shipping = 0) => {
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const tax = subtotal * 0.16; // 16% VAT in Kenya
  
  let discount = 0;
  if (appliedCoupon) {
    if (appliedCoupon.type === 'percentage') {
      discount = subtotal * (appliedCoupon.value / 100);
    } else if (appliedCoupon.type === 'fixed') {
      discount = appliedCoupon.value;
    }
    discount = Math.min(discount, appliedCoupon.maxDiscount || discount);
  }
  
  const total = subtotal + tax + shipping - discount;
  
  return {
    subtotal,
    tax,
    shipping,
    discount,
    total: Math.max(0, total),
  };
};

// Cart Slice
const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    toggleCart: (state) => {
      state.isOpen = !state.isOpen;
    },
    openCart: (state) => {
      state.isOpen = true;
    },
    closeCart: (state) => {
      state.isOpen = false;
    },
    setDeliveryOption: (state, action) => {
      state.deliveryOption = action.payload;
      
      // Calculate shipping cost based on delivery option
      let shippingCost = 0;
      switch (action.payload) {
        case 'express':
          shippingCost = 500;
          break;
        case 'standard':
          shippingCost = 200;
          break;
        case 'pickup':
          shippingCost = 0;
          break;
        default:
          shippingCost = 200;
      }
      
      state.shipping = shippingCost;
      const totals = calculateTotals(state.items, state.appliedCoupon, shippingCost);
      Object.assign(state, totals);
    },
    setInstallationOption: (state, action) => {
      const { productId, option } = action.payload;
      state.installationOptions[productId] = option;
    },
    // Local cart operations (for offline use)
    addItemLocal: (state, action) => {
      const { product, quantity = 1, options = {} } = action.payload;
      const existingItem = state.items.find(item => 
        item.productId === product._id && 
        JSON.stringify(item.options) === JSON.stringify(options)
      );
      
      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        state.items.push({
          productId: product._id,
          product,
          quantity,
          options,
          price: product.price,
          image: product.images[0],
          name: product.name,
        });
      }
      
      state.totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);
      const totals = calculateTotals(state.items, state.appliedCoupon, state.shipping);
      Object.assign(state, totals);
    },
    updateItemLocal: (state, action) => {
      const { productId, quantity, options } = action.payload;
      const item = state.items.find(item => 
        item.productId === productId && 
        JSON.stringify(item.options) === JSON.stringify(options)
      );
      
      if (item) {
        if (quantity <= 0) {
          state.items = state.items.filter(i => i !== item);
        } else {
          item.quantity = quantity;
        }
      }
      
      state.totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);
      const totals = calculateTotals(state.items, state.appliedCoupon, state.shipping);
      Object.assign(state, totals);
    },
    removeItemLocal: (state, action) => {
      const productId = action.payload;
      state.items = state.items.filter(item => item.productId !== productId);
      state.totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);
      const totals = calculateTotals(state.items, state.appliedCoupon, state.shipping);
      Object.assign(state, totals);
    },
    clearCartLocal: (state) => {
      state.items = [];
      state.totalItems = 0;
      state.appliedCoupon = null;
      state.installationOptions = {};
      const totals = calculateTotals([], null, state.shipping);
      Object.assign(state, totals);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Cart
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        const cart = action.payload;
        state.items = cart.items || [];
        state.totalItems = cart.totalItems || 0;
        state.appliedCoupon = cart.appliedCoupon || null;
        state.deliveryOption = cart.deliveryOption || 'standard';
        state.installationOptions = cart.installationOptions || {};
        
        const totals = calculateTotals(state.items, state.appliedCoupon, state.shipping);
        Object.assign(state, totals);
        state.error = null;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Add to Cart
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        const cart = action.payload;
        state.items = cart.items;
        state.totalItems = cart.totalItems;
        
        const totals = calculateTotals(state.items, state.appliedCoupon, state.shipping);
        Object.assign(state, totals);
        state.error = null;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Cart Item
      .addCase(updateCartItem.fulfilled, (state, action) => {
        const cart = action.payload;
        state.items = cart.items;
        state.totalItems = cart.totalItems;
        
        const totals = calculateTotals(state.items, state.appliedCoupon, state.shipping);
        Object.assign(state, totals);
      })
      
      // Remove from Cart
      .addCase(removeFromCart.fulfilled, (state, action) => {
        const { cart } = action.payload;
        state.items = cart.items;
        state.totalItems = cart.totalItems;
        
        const totals = calculateTotals(state.items, state.appliedCoupon, state.shipping);
        Object.assign(state, totals);
      })
      
      // Clear Cart
      .addCase(clearCart.fulfilled, (state) => {
        state.items = [];
        state.totalItems = 0;
        state.appliedCoupon = null;
        state.installationOptions = {};
        
        const totals = calculateTotals([], null, state.shipping);
        Object.assign(state, totals);
      })
      
      // Apply Coupon
      .addCase(applyCoupon.fulfilled, (state, action) => {
        const { coupon } = action.payload;
        state.appliedCoupon = coupon;
        
        const totals = calculateTotals(state.items, coupon, state.shipping);
        Object.assign(state, totals);
      })
      
      // Remove Coupon
      .addCase(removeCoupon.fulfilled, (state) => {
        state.appliedCoupon = null;
        
        const totals = calculateTotals(state.items, null, state.shipping);
        Object.assign(state, totals);
      });
  },
});

export const {
  clearError,
  toggleCart,
  openCart,
  closeCart,
  setDeliveryOption,
  setInstallationOption,
  addItemLocal,
  updateItemLocal,
  removeItemLocal,
  clearCartLocal,
} = cartSlice.actions;

export default cartSlice.reducer;